// frontend/src/pages/BookDetail.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
    BookOpen, User, Edit, Globe, FileText, Tag, Loader2, RefreshCw, AlertTriangle, ZoomIn, ZoomOut
} from 'lucide-react';
import { fetchBookBySlug } from '../services/bookService';
import RelatedProductsByBookTags from '../components/library/RelatedProductsByBookTags';
import { sanitizeHtml } from '../utils/sanitizeHtml';

// ----------------------------------------------------------------------
// کامپوننت نمایش مشخصات (فشرده‌تر)
// ----------------------------------------------------------------------
const SpecItem = ({ icon: Icon, label, value }) => {
    if (!value || (typeof value === 'string' && !value.trim())) return null;
    return (
        <div className="flex items-center text-[11px] text-gray-600">
            <Icon size={14} className="ml-1.5 text-gray-400 flex-shrink-0" />
            <span className="font-medium ml-1">{label}:</span>
            <span className="truncate break-words whitespace-normal">{value}</span>
        </div>
    );
};

// ----------------------------------------------------------------------
// کامپوننت‌های وضعیت‌های بارگذاری و خطا
// ----------------------------------------------------------------------
const LoadingState = ({ slug }) => (
    <div className="flex flex-col justify-center items-center p-20 min-h-screen bg-gray-50">
        <Loader2 size={40} className="animate-spin text-indigo-600 mb-4" />
        <span className="text-[12px] text-indigo-600 font-medium">
            در حال بارگذاری جزئیات کتاب {slug}...
        </span>
    </div>
);

const ErrorState = ({ error, onRetry }) => (
    <div className="text-center p-20 text-red-700 font-bold bg-red-100 rounded-xl max-w-lg mx-auto border border-red-300 min-h-[400px] flex flex-col justify-center">
        <h3 className="text-2xl mb-4">😔 خطا در بارگذاری</h3>
        <p className="mb-6">متأسفانه در واکشی اطلاعات خطایی رخ داد:</p>
        <p className="text-[11px] text-justify italic font-normal text-red-600 border-t pt-3 mt-3">{error}</p>
        <button 
            onClick={onRetry} 
            className="mt-6 mx-auto flex items-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
        >
            <RefreshCw size={16} className="ml-2" />
            تلاش مجدد
        </button>
    </div>
);

// ----------------------------------------------------------------------
// کامپوننت اصلی BookDetail
// ----------------------------------------------------------------------
const BookDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [book, setBook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const topRef = useRef(null);
    
    const [fontSize, setFontSize] = useState(() => {
        const saved = localStorage.getItem('bookSummaryFontSize');
        return saved ? parseInt(saved) : 16;
    });

    const loadData = useCallback(async () => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        if (!slug) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const detailResponse = await fetchBookBySlug(slug);
            const detail = detailResponse.data;
            setBook(detail);
        } catch (err) {
            console.error("خطا در بارگذاری جزئیات کتاب:", err);
            const errorMessage = err.message || err.response?.data?.detail || "خطای نامشخص در واکشی داده.";
            setError(errorMessage);
            setBook(null);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const increaseFont = () => {
        if (fontSize < 28) {
            const newSize = fontSize + 2;
            setFontSize(newSize);
            localStorage.setItem('bookSummaryFontSize', newSize);
        }
    };

    const decreaseFont = () => {
        if (fontSize > 12) {
            const newSize = fontSize - 2;
            setFontSize(newSize);
            localStorage.setItem('bookSummaryFontSize', newSize);
        }
    };

    const handleTagClick = (tagSlug) => {
        navigate(`/books?tags__slug=${tagSlug}`);
    };

    if (loading) return <LoadingState slug={slug} />;
    if (error || !book) return <ErrorState error={error} onRetry={loadData} />;

    const {
        title,
        cover_image_url,
        author,
        translator,
        publisher,
        tags,
        page_count,
        summary,
        order_link,
    } = book;

    const finalOrderLink = order_link || '#';
    const bookTags = tags?.map(tag => tag.title) || [];
    
    // تولید توضیحات متا (نخستین ۱۵۰ کاراکتر خلاصه، بدون HTML)
    let plainSummary = '';
    if (summary) {
        plainSummary = summary.replace(/<[^>]*>/g, '').trim();
        if (plainSummary.length > 155) {
            plainSummary = plainSummary.substring(0, 155) + '...';
        }
    } else {
        plainSummary = 'خلاصه کتاب';
    }
    
    const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
    const ogImage = cover_image_url || defaultOgImage;
    const metaTitle = `${title} | فرزانه`;

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={plainSummary} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={plainSummary} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:type" content="book" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={plainSummary} />
                <meta name="twitter:image" content={ogImage} />
                <meta name="robots" content="index, follow" />
            </Helmet>

            <div ref={topRef} className="p-3 min-h-screen rtl text-right">
                <div className="max-w-6xl mx-auto">
                    {/* کارت اصلی کتاب - دو ستون */}
                    <div className="bg-white p-3 rounded-xl border border-gray-200 mb-5">
                        <div className="flex flex-row gap-4">
                            {/* ستون تصویر (عرض کمتر) */}
                            <div className="w-2/5 flex-shrink-0">
                                <div className="w-full bg-white p-1 rounded-lg">
                                    <img
                                        src={cover_image_url}
                                        alt={`کاور ${title}`}
                                        className="w-full h-auto object-contain rounded-md shadow-md max-h-[350px] mx-auto"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = `https://placehold.co/400x600/e2e8f0/94a3b8?text=${encodeURIComponent(title)}`;
                                        }}
                                    />
                                </div>
                            </div>

                            {/* ستون اطلاعات (فشرده) */}
                            <div className="w-3/5 flex flex-col space-y-3">
                                <h1 className="text-lg font-extrabold text-gray-900">
                                    {title}
                                </h1>

                                <div className="space-y-1 p-3 rounded-lg bg-gray-50">
                                    <h3 className="text-[10px] font-semibold text-gray-800">مشخصات اصلی</h3>
                                    <div className="grid grid-cols-1 gap-1">
                                        {author?.name && author.name.trim() && (
                                            <SpecItem icon={User} label="نویسنده" value={author.name} />
                                        )}
                                        {translator?.name && translator.name.trim() && (
                                            <SpecItem icon={Edit} label="مترجم" value={translator.name} />
                                        )}
                                        {publisher?.title && publisher.title.trim() && (
                                            <SpecItem icon={Globe} label="ناشر" value={publisher.title} />
                                        )}
                                        {page_count && (
                                            <SpecItem icon={FileText} label="صفحات" value={page_count} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* تگ‌ها - در یک ردیف جداگانه زیر تصویر و عنوان */}
                        {tags?.length > 0 && (
                            <div className="mt-4 pt-2 border-t border-gray-100">
                                <div className="flex flex-wrap gap-2 justify-start">
                                    {tags.map(tag => (
                                        <span
                                            key={tag.slug}
                                            onClick={() => handleTagClick(tag.slug)}
                                            className="flex items-center text-[10px] font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
                                        >
                                            <Tag size={12} className="ml-1" />
                                            {tag.title}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* بخش خلاصه کتاب (بدون تغییر) */}
                    <div className="bg-white p-5 rounded-xl mb-8 border border-gray-200">
                        <div className="flex justify-end items-center gap-3 mb-3">
                            <button onClick={decreaseFont} disabled={fontSize <= 12} className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-40 transition">
                                <ZoomOut size={20} />
                            </button>
                            <span className="text-xs text-gray-600">{fontSize}px</span>
                            <button onClick={increaseFont} disabled={fontSize >= 28} className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-40 transition">
                                <ZoomIn size={20} />
                            </button>
                        </div>
                        <h3 className="text-[14px] font-thin text-gray-800 mb-3 border-b pb-2">درباره‌یِ کتاب</h3>
                        <div
                            className="content-area text-gray-700 leading-relaxed text-justify"
                            style={{ fontSize: `${fontSize}px` }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(book.summary || "خلاصه این کتاب هنوز ارائه نشده است.") }}
                        />
                    </div>

                    {/* محصولات مرتبط */}
                    {bookTags.length > 0 && slug && (
                        <div className="mt-10 mb-8 max-w-[460px] mx-auto">
                            <RelatedProductsByBookTags tags={bookTags} currentBookSlug={slug} />
                        </div>
                    )}

                    {bookTags.length === 0 && (
                        <div className="mt-8 p-4 bg-yellow-50 text-right text-yellow-700 rounded-lg flex items-center max-w-[460px] mx-auto">
                            <AlertTriangle size={20} className="ml-2 flex-shrink-0" />
                            <span>برای این کتاب تگی تعریف نشده است، بنابراین محتوای مرتبطی نمایش داده نشد.</span>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default BookDetail;